/**
 * 問診票形式のフォームセクションコンポーネント
 * 健康心理条件の入力フォームを問診票の書式に合わせて表示
 */

import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Textarea } from '../../components/ui/textarea';

interface QuestionnaireFormData {
  [key: string]: string | string[] | boolean;
}

interface QuestionnaireFormSectionsProps {
  formData: QuestionnaireFormData;
  onChange: (key: string, value: string | string[] | boolean) => void;
}

/**
 * [１] いかがされましたか？
 */
export function Complaint1Section({ formData, onChange }: QuestionnaireFormSectionsProps) {
  const symptoms = [
    '歯が痛い',
    '虫歯がある',
    '歯ぐきが腫れた',
    '歯石が気になる',
    '詰め物が取れた',
    '義歯が壊れた',
    '歯を入れてもらいたい',
  ];

  const handleSymptomChange = (symptom: string, checked: boolean) => {
    const currentSymptoms = (formData['complaint1_symptoms'] as string[]) || [];
    if (checked) {
      onChange('complaint1_symptoms', [...currentSymptoms, symptom]);
    } else {
      onChange('complaint1_symptoms', currentSymptoms.filter(s => s !== symptom));
    }
  };

  const handleOtherChange = (value: string) => {
    onChange('complaint1_other', value);
  };

  return (
    <div className="space-y-4">
      <Label className="text-lg font-semibold">[１] いかがされましたか？</Label>
      <div className="space-y-2 pl-4">
        {symptoms.map((symptom) => (
          <div key={symptom} className="flex items-center space-x-2">
            <Checkbox
              id={`complaint1_${symptom}`}
              checked={(formData['complaint1_symptoms'] as string[] || []).includes(symptom)}
              onCheckedChange={(checked) => handleSymptomChange(symptom, checked as boolean)}
            />
            <Label htmlFor={`complaint1_${symptom}`} className="font-normal cursor-pointer">
              {symptom}
            </Label>
          </div>
        ))}
        <div className="space-y-2">
          <Label>その他</Label>
          <Textarea
            value={(formData['complaint1_other'] as string) || ''}
            onChange={(e) => handleOtherChange(e.target.value)}
            placeholder="その他の症状を入力してください"
            rows={2}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * [２] 通院中・治療中の病気はありますか？
 */
export function Complaint2Section({ formData, onChange }: QuestionnaireFormSectionsProps) {
  const diseases = [
    { key: 'none', label: 'ない' },
    { key: 'hypertension', label: '高血圧' },
    { key: 'heart_disease', label: '心臓病' },
    { key: 'diabetes', label: '糖尿病' },
    { key: 'stroke', label: '脳卒中' },
    { key: 'asthma', label: 'ぜんそく' },
    { key: 'liver_disease', label: '肝臓病' },
    { key: 'kidney_disease', label: '腎臓病' },
    { key: 'epilepsy', label: 'てんかん' },
    { key: 'allergy', label: 'アレルギー' },
    { key: 'thyroid', label: '甲状腺の病気' },
    { key: 'cancer', label: 'がん' },
    { key: 'dementia', label: '認知症' },
    { key: 'hiv', label: 'HIV' },
  ];

  const heartDiseaseDetails = [
    '狭心症',
    '心筋梗塞',
    '不整脈',
    '弁膜症',
    '心不全',
    '心筋症',
    '先天性心疾患',
  ];

  const handleDiseaseChange = (diseaseKey: string, checked: boolean) => {
    const currentDiseases = (formData['complaint2_diseases'] as string[]) || [];
    if (checked) {
      onChange('complaint2_diseases', [...currentDiseases, diseaseKey]);
    } else {
      onChange('complaint2_diseases', currentDiseases.filter(d => d !== diseaseKey));
    }
  };

  const handleHeartDetailChange = (detail: string, checked: boolean) => {
    const currentDetails = (formData['complaint2_heart_details'] as string[]) || [];
    if (checked) {
      onChange('complaint2_heart_details', [...currentDetails, detail]);
    } else {
      onChange('complaint2_heart_details', currentDetails.filter(d => d !== detail));
    }
  };

  const handleOtherChange = (value: string) => {
    onChange('complaint2_other', value);
  };

  const hasHeartDisease = (formData['complaint2_diseases'] as string[] || []).includes('heart_disease');

  return (
    <div className="space-y-4">
      <Label className="text-lg font-semibold">[２] 通院中・治療中の病気はありますか？</Label>
      <div className="space-y-2 pl-4">
        {diseases.map((disease) => (
          <div key={disease.key} className="flex items-center space-x-2">
            <Checkbox
              id={`complaint2_${disease.key}`}
              checked={(formData['complaint2_diseases'] as string[] || []).includes(disease.key)}
              onCheckedChange={(checked) => handleDiseaseChange(disease.key, checked as boolean)}
            />
            <Label htmlFor={`complaint2_${disease.key}`} className="font-normal cursor-pointer">
              {disease.label}
            </Label>
            {disease.key === 'heart_disease' && hasHeartDisease && (
              <div className="ml-4 space-y-1">
                <span className="text-sm">（</span>
                {heartDiseaseDetails.map((detail) => (
                  <div key={detail} className="flex items-center space-x-2 ml-4">
                    <Checkbox
                      id={`complaint2_heart_${detail}`}
                      checked={(formData['complaint2_heart_details'] as string[] || []).includes(detail)}
                      onCheckedChange={(checked) => handleHeartDetailChange(detail, checked as boolean)}
                    />
                    <Label htmlFor={`complaint2_heart_${detail}`} className="font-normal cursor-pointer text-sm">
                      {detail}
                    </Label>
                  </div>
                ))}
                <span className="text-sm">）</span>
              </div>
            )}
          </div>
        ))}
        <div className="space-y-2">
          <Label>その他</Label>
          <Textarea
            value={(formData['complaint2_other'] as string) || ''}
            onChange={(e) => handleOtherChange(e.target.value)}
            placeholder="その他の病気を入力してください"
            rows={2}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * [３] 現在服用中の薬剤はありますか？
 */
export function Complaint3Section({ formData, onChange }: QuestionnaireFormSectionsProps) {
  const hasMedication = formData['complaint3_has_medication'] as string || 'none';

  const handleMedicationChange = (value: string) => {
    onChange('complaint3_has_medication', value);
  };

  const handleMedicationBookChange = (checked: boolean) => {
    onChange('complaint3_has_medication_book', checked);
  };

  const handleMedicationNameChange = (value: string) => {
    onChange('complaint3_medication_name', value);
  };

  return (
    <div className="space-y-4">
      <Label className="text-lg font-semibold">[３] 現在服用中の薬剤はありますか？</Label>
      <div className="space-y-4 pl-4">
        <RadioGroup value={hasMedication} onValueChange={handleMedicationChange}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="none" id="complaint3_none" />
            <Label htmlFor="complaint3_none" className="font-normal cursor-pointer">ない</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="complaint3_yes" />
            <Label htmlFor="complaint3_yes" className="font-normal cursor-pointer">ある</Label>
          </div>
        </RadioGroup>
        {hasMedication === 'yes' && (
          <div className="ml-4 space-y-4">
            <div className="space-y-2">
              <Label>持ってきているもの</Label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="complaint3_medication_book"
                    checked={formData['complaint3_has_medication_book'] as boolean || false}
                    onCheckedChange={handleMedicationBookChange}
                  />
                  <Label htmlFor="complaint3_medication_book" className="font-normal cursor-pointer">
                    お薬手帳
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="complaint3_medication"
                    checked={formData['complaint3_has_medication_item'] as boolean || false}
                    onCheckedChange={(checked) => onChange('complaint3_has_medication_item', checked as boolean)}
                  />
                  <Label htmlFor="complaint3_medication" className="font-normal cursor-pointer">
                    お薬
                  </Label>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>お薬の名前</Label>
              <Textarea
                value={(formData['complaint3_medication_name'] as string) || ''}
                onChange={(e) => handleMedicationNameChange(e.target.value)}
                placeholder="服用中の薬剤の名前を入力してください"
                rows={2}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * [４] 生活の中で気になるおからだの状態
 */
export function Complaint4Section({ formData, onChange }: QuestionnaireFormSectionsProps) {
  const handleBadFeelingChange = (value: string) => {
    onChange('complaint4_bad_feeling', value);
  };

  const handleAllergyChange = (value: string) => {
    onChange('complaint4_allergy', value);
  };

  const handlePregnancyChange = (value: string) => {
    onChange('complaint4_pregnancy', value);
  };

  const handleOtherChange = (value: string) => {
    onChange('complaint4_other', value);
  };

  return (
    <div className="space-y-4">
      <Label className="text-lg font-semibold">[４] 生活の中で気になる おからだ の状態を教えてください。</Label>
      <div className="space-y-4 pl-4">
        <div className="space-y-2">
          <Label>①歯科治療中に気分が悪くなったことがありますか？</Label>
          <RadioGroup
            value={(formData['complaint4_bad_feeling'] as string) || ''}
            onValueChange={handleBadFeelingChange}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="complaint4_bad_feeling_yes" />
              <Label htmlFor="complaint4_bad_feeling_yes" className="font-normal cursor-pointer">はい</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="complaint4_bad_feeling_no" />
              <Label htmlFor="complaint4_bad_feeling_no" className="font-normal cursor-pointer">いいえ</Label>
            </div>
          </RadioGroup>
        </div>
        <div className="space-y-2">
          <Label>②薬のアレルギーはありますか？</Label>
          <RadioGroup
            value={(formData['complaint4_allergy'] as string) || ''}
            onValueChange={handleAllergyChange}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="complaint4_allergy_yes" />
              <Label htmlFor="complaint4_allergy_yes" className="font-normal cursor-pointer">はい</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="suspected" id="complaint4_allergy_suspected" />
              <Label htmlFor="complaint4_allergy_suspected" className="font-normal cursor-pointer">可能性・疑いあり</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="complaint4_allergy_no" />
              <Label htmlFor="complaint4_allergy_no" className="font-normal cursor-pointer">いいえ</Label>
            </div>
          </RadioGroup>
        </div>
        <div className="space-y-2">
          <Label>③妊娠・授乳中ですか？</Label>
          <RadioGroup
            value={(formData['complaint4_pregnancy'] as string) || 'no'}
            onValueChange={handlePregnancyChange}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="complaint4_pregnancy_yes" />
              <Label htmlFor="complaint4_pregnancy_yes" className="font-normal cursor-pointer">はい</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="suspected" id="complaint4_pregnancy_suspected" />
              <Label htmlFor="complaint4_pregnancy_suspected" className="font-normal cursor-pointer">可能性・疑いあり</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="complaint4_pregnancy_no" />
              <Label htmlFor="complaint4_pregnancy_no" className="font-normal cursor-pointer">いいえ</Label>
            </div>
          </RadioGroup>
        </div>
        <div className="space-y-2">
          <Label>その他</Label>
          <Textarea
            value={(formData['complaint4_other'] as string) || ''}
            onChange={(e) => handleOtherChange(e.target.value)}
            placeholder="その他の気になる状態を入力してください"
            rows={2}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * [５] 生活習慣について
 */
export function Complaint5Section({ formData, onChange }: QuestionnaireFormSectionsProps) {
  const brushingTimes = [
    { key: 'morning', label: '起床後' },
    { key: 'after_breakfast', label: '朝食後' },
    { key: 'after_lunch', label: '昼食後' },
    { key: 'after_dinner', label: '夕食後' },
    { key: 'before_bed', label: '就寝前' },
  ];

  const brushingTools = [
    { key: 'toothbrush', label: '歯ブラシ' },
    { key: 'floss', label: 'フロス' },
    { key: 'interdental', label: '歯間ブラシ' },
  ];

  const handleBrushingTimeChange = (timeKey: string, checked: boolean) => {
    const currentTimes = (formData['complaint5_brushing_times'] as string[]) || [];
    if (checked) {
      onChange('complaint5_brushing_times', [...currentTimes, timeKey]);
    } else {
      onChange('complaint5_brushing_times', currentTimes.filter(t => t !== timeKey));
    }
  };

  const handleBrushingDurationChange = (value: string) => {
    onChange('complaint5_brushing_duration', value);
  };

  const handleBrushingToolChange = (toolKey: string, checked: boolean) => {
    const currentTools = (formData['complaint5_brushing_tools'] as string[]) || [];
    if (checked) {
      onChange('complaint5_brushing_tools', [...currentTools, toolKey]);
    } else {
      onChange('complaint5_brushing_tools', currentTools.filter(t => t !== toolKey));
    }
  };

  const handleSmokingChange = (value: string) => {
    onChange('complaint5_smoking', value);
  };

  const handleSleepHoursChange = (value: string) => {
    onChange('complaint5_sleep_hours', value);
  };

  const handleDrinksChange = (value: string) => {
    onChange('complaint5_has_drinks', value);
  };

  const handleDrinksNameChange = (value: string) => {
    onChange('complaint5_drinks_name', value);
  };

  const handleSnackingChange = (value: string) => {
    onChange('complaint5_snacking', value);
  };

  return (
    <div className="space-y-4">
      <Label className="text-lg font-semibold">[５] 生活習慣について教えてください。</Label>
      <div className="space-y-4 pl-4">
        {/* ①歯磨きは */}
        <div className="space-y-2">
          <Label>①歯磨きは、</Label>
          <div className="ml-4 space-y-2">
            <div className="space-y-2">
              <Label className="text-sm">【時間帯】</Label>
              <div className="space-y-1">
                {brushingTimes.map((time) => (
                  <div key={time.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={`complaint5_brushing_time_${time.key}`}
                      checked={(formData['complaint5_brushing_times'] as string[] || []).includes(time.key)}
                      onCheckedChange={(checked) => handleBrushingTimeChange(time.key, checked as boolean)}
                    />
                    <Label htmlFor={`complaint5_brushing_time_${time.key}`} className="font-normal cursor-pointer text-sm">
                      {time.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">【1回の時間】</Label>
              <div className="flex items-center space-x-2">
                <span className="text-sm">（</span>
                <Input
                  type="number"
                  value={(formData['complaint5_brushing_duration'] as string) || ''}
                  onChange={(e) => handleBrushingDurationChange(e.target.value)}
                  placeholder="分"
                  className="w-20"
                />
                <span className="text-sm">）分程度</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">【使うもの】</Label>
              <div className="space-y-1">
                {brushingTools.map((tool) => (
                  <div key={tool.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={`complaint5_brushing_tool_${tool.key}`}
                      checked={(formData['complaint5_brushing_tools'] as string[] || []).includes(tool.key)}
                      onCheckedChange={(checked) => handleBrushingToolChange(tool.key, checked as boolean)}
                    />
                    <Label htmlFor={`complaint5_brushing_tool_${tool.key}`} className="font-normal cursor-pointer text-sm">
                      {tool.label}
                    </Label>
                  </div>
                ))}
                <div className="space-y-2 ml-4">
                  <Label className="text-sm">その他</Label>
                  <Textarea
                    value={(formData['complaint5_brushing_tools_other'] as string) || ''}
                    onChange={(e) => onChange('complaint5_brushing_tools_other', e.target.value)}
                    placeholder="その他の清掃用具を入力してください"
                    rows={1}
                    className="text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ②喫煙習慣は */}
        <div className="space-y-2">
          <Label>②喫煙習慣は、</Label>
          <RadioGroup
            value={(formData['complaint5_smoking'] as string) || ''}
            onValueChange={handleSmokingChange}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="none" id="complaint5_smoking_none" />
              <Label htmlFor="complaint5_smoking_none" className="font-normal cursor-pointer">なし</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="complaint5_smoking_yes" />
              <Label htmlFor="complaint5_smoking_yes" className="font-normal cursor-pointer">あり</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="past" id="complaint5_smoking_past" />
              <Label htmlFor="complaint5_smoking_past" className="font-normal cursor-pointer">過去にあり</Label>
            </div>
          </RadioGroup>
        </div>

        {/* ③睡眠時間は */}
        <div className="space-y-2">
          <Label>③睡眠時間は、１日平均</Label>
          <div className="flex items-center space-x-2">
            <span className="text-sm">（</span>
            <Input
              type="number"
              value={(formData['complaint5_sleep_hours'] as string) || ''}
              onChange={(e) => handleSleepHoursChange(e.target.value)}
              placeholder="時間"
              className="w-20"
            />
            <span className="text-sm">）時間程度</span>
          </div>
        </div>

        {/* ④習慣的によく飲むものは */}
        <div className="space-y-2">
          <Label>④習慣的によく飲むものは、</Label>
          <RadioGroup
            value={(formData['complaint5_has_drinks'] as string) || ''}
            onValueChange={handleDrinksChange}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="none" id="complaint5_drinks_none" />
              <Label htmlFor="complaint5_drinks_none" className="font-normal cursor-pointer">なし</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="complaint5_drinks_yes" />
              <Label htmlFor="complaint5_drinks_yes" className="font-normal cursor-pointer">あり</Label>
            </div>
          </RadioGroup>
          {(formData['complaint5_has_drinks'] as string) === 'yes' && (
            <div className="ml-4 space-y-2">
              <Textarea
                value={(formData['complaint5_drinks_name'] as string) || ''}
                onChange={(e) => handleDrinksNameChange(e.target.value)}
                placeholder="よく飲む飲み物を入力してください"
                rows={1}
              />
            </div>
          )}
        </div>

        {/* ⑤間食は */}
        <div className="space-y-2">
          <Label>⑤間食は、</Label>
          <RadioGroup
            value={(formData['complaint5_snacking'] as string) || ''}
            onValueChange={handleSnackingChange}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="none" id="complaint5_snacking_none" />
              <Label htmlFor="complaint5_snacking_none" className="font-normal cursor-pointer">しない</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="regular" id="complaint5_snacking_regular" />
              <Label htmlFor="complaint5_snacking_regular" className="font-normal cursor-pointer">規則正しく摂る</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="irregular" id="complaint5_snacking_irregular" />
              <Label htmlFor="complaint5_snacking_irregular" className="font-normal cursor-pointer">不規則に摂る</Label>
            </div>
          </RadioGroup>
        </div>
      </div>
    </div>
  );
}

/**
 * [６] ご希望の治療
 */
export function Complaint6Section({ formData, onChange }: QuestionnaireFormSectionsProps) {
  const treatments = [
    '悪いところは全部治療したい',
    '痛いところだけ、希望するところだけ治したい',
    '歯の磨き方などについても教えて欲しい',
  ];

  const handleTreatmentChange = (treatment: string, checked: boolean) => {
    const currentTreatments = (formData['complaint6_treatments'] as string[]) || [];
    if (checked) {
      onChange('complaint6_treatments', [...currentTreatments, treatment]);
    } else {
      onChange('complaint6_treatments', currentTreatments.filter(t => t !== treatment));
    }
  };

  return (
    <div className="space-y-4">
      <Label className="text-lg font-semibold">[６] ご希望の治療を教えてください。</Label>
      <div className="space-y-2 pl-4">
        {treatments.map((treatment) => (
          <div key={treatment} className="flex items-center space-x-2">
            <Checkbox
              id={`complaint6_${treatment}`}
              checked={(formData['complaint6_treatments'] as string[] || []).includes(treatment)}
              onCheckedChange={(checked) => handleTreatmentChange(treatment, checked as boolean)}
            />
            <Label htmlFor={`complaint6_${treatment}`} className="font-normal cursor-pointer">
              {treatment}
            </Label>
          </div>
        ))}
      </div>
    </div>
  );
}

